import { useState, useEffect, useMemo } from "react";
import { Shield, Warning, X, CircleNotch, CheckCircle, WarningOctagon } from "@phosphor-icons/react";
import api, { fmtMoney, fmtNum, formatErr } from "@/lib/api";
import { toast } from "sonner";

export default function HedgeSquareOffModal({
  isOpen,
  onClose,
  mode = "single", // "single" | "selected" | "all"
  trade = null, // required for single mode
  selectedTrades = [], // required for selected mode
  selectedAccountIds = [], // for bulk mode
  allTrades = [], // optional running trades array
  activeHedgesCount = 0,
  totalHedgeLots = 0.0,
  overallHedgeMtm = 0.0,
  onSuccess = () => {},
}) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [fetchedTrades, setFetchedTrades] = useState([]);
  const [fetching, setFetching] = useState(false);

  const accountIdsKey = (selectedAccountIds || []).join(",");
  const hasAllTrades = Boolean(allTrades && allTrades.length > 0);

  useEffect(() => {
    if (!isOpen) {
      setFetchedTrades([]);
      setResult(null);
      setFetching(false);
      return;
    }

    if (!hasAllTrades && accountIdsKey) {
      let mounted = true;
      setFetching(true);
      const accIds = accountIdsKey.split(",").filter(Boolean);
      (async () => {
        try {
          const list = [];
          for (const accId of accIds) {
            const { data } = await api.get(`/accounts/${accId}/trades`);
            if (data && data.open) {
              list.push(...data.open.map((t) => ({ ...t, account_id: t.account_id || accId })));
            }
          }
          if (mounted) setFetchedTrades(list);
        } catch (e) {
          console.error("Failed to fetch open trades for modal:", e);
        } finally {
          if (mounted) setFetching(false);
        }
      })();
      return () => {
        mounted = false;
      };
    }
  }, [isOpen, accountIdsKey, hasAllTrades]);

  const runningTrades = hasAllTrades ? allTrades : fetchedTrades;

  const hedgePositions = useMemo(() => {
    return runningTrades.filter(
      (t) => Boolean(t.is_hedge === true || String(t.is_hedge).toLowerCase() === "true" || t.magic === 90001 || String(t.magic) === "90001" || t.magic === 999999 || String(t.magic) === "999999" || ["HEDGE", "HDG", "HUB"].some((kw) => String(t.comment || "").toUpperCase().includes(kw)))
    );
  }, [runningTrades]);

  const mainPositions = useMemo(() => {
    return runningTrades.filter(
      (t) => !Boolean(t.is_hedge === true || String(t.is_hedge).toLowerCase() === "true" || t.magic === 90001 || String(t.magic) === "90001" || t.magic === 999999 || String(t.magic) === "999999" || ["HEDGE", "HDG", "HUB"].some((kw) => String(t.comment || "").toUpperCase().includes(kw)))
    );
  }, [runningTrades]);

  if (!isOpen) return null;

  const effectiveHedgeCount = runningTrades.length > 0 ? hedgePositions.length : activeHedgesCount;
  const effectiveHedgeLots = runningTrades.length > 0
    ? hedgePositions.reduce((sum, t) => sum + Number(t.lot || 0), 0)
    : totalHedgeLots;
  const effectiveHedgeMtm = runningTrades.length > 0
    ? hedgePositions.reduce((sum, t) => sum + (Number(t.profit || 0) + Number(t.swap || 0) + Number(t.commission || 0)), 0)
    : overallHedgeMtm;

  const handleConfirmSingle = async () => {
    if (!trade || loading) return;
    setLoading(true);
    setResult(null);
    try {
      const ticket = trade.ticket ?? trade.id;
      const { data } = await api.post("/orders/close-single-hedge", {
        account_id: trade.account_id,
        ticket: Number(ticket),
      });
      toast.success(data.message || `Square-off queued for hedge trade #${ticket}`);
      onSuccess();
      onClose();
    } catch (e) {
      const errDetail = formatErr(e.response?.data?.detail) || "Failed to square off hedge position";
      toast.error(errDetail);
      setResult({ success: false, message: errDetail });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSelected = async () => {
    if (!selectedTrades || selectedTrades.length === 0 || loading) return;
    setLoading(true);
    setResult(null);
    try {
      const payload = selectedTrades.map((t) => ({
        account_id: t.account_id,
        ticket: Number(t.ticket ?? t.id),
      }));
      const { data } = await api.post("/orders/close-selected-hedges", { trades: payload });
      toast.success(data.message || `Square-off queued for ${data.total_queued} selected hedge trade(s)`);
      setResult({
        success: true,
        summary: data,
        message: data.message,
      });
      onSuccess();
    } catch (e) {
      const errDetail = formatErr(e.response?.data?.detail) || "Failed to square off selected hedge positions";
      toast.error(errDetail);
      setResult({ success: false, message: errDetail });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAll = async () => {
    if (loading) return;
    if (!selectedAccountIds || selectedAccountIds.length === 0) {
      toast.error("No target account selected for hedge square-off.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/orders/close-all-hedges", {
        account_ids: selectedAccountIds,
      });
      toast.success(`Square-off all hedges queued for ${data.account_count || selectedAccountIds.length} account(s)`);
      setResult({
        success: true,
        summary: data,
      });
      onSuccess();
    } catch (e) {
      const errDetail = formatErr(e.response?.data?.detail) || "Failed to square off hedge positions";
      toast.error(errDetail);
      setResult({ success: false, message: errDetail });
    } finally {
      setLoading(false);
    }
  };

  const isBuy = trade ? (trade.side || "buy").toLowerCase() === "buy" : false;
  const mtm = trade ? Number(trade.profit || 0) + Number(trade.swap || 0) + Number(trade.commission || 0) : effectiveHedgeMtm;

  const selectedCount = selectedTrades.length;
  const selectedLots = selectedTrades.reduce((sum, t) => sum + Number(t.lot || 0), 0);
  const selectedMtm = selectedTrades.reduce(
    (sum, t) => sum + (Number(t.profit || 0) + Number(t.swap || 0) + Number(t.commission || 0)),
    0
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm animate-in fade-in duration-150">
      <div
        className="w-full max-w-xl bg-app-surface border border-app-border rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="p-4 border-b border-app-border flex items-center justify-between bg-app-bg/50 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-[#38BDF8]/10 text-[#38BDF8] border border-[#38BDF8]/20">
              <Shield size={20} weight="fill" />
            </div>
            <div>
              <h3 className="font-mono-d text-sm font-bold uppercase tracking-wider text-app-text">
                {mode === "single"
                  ? "Square Off Single Hedge"
                  : mode === "selected"
                  ? `Square Off ${selectedCount} Selected Hedge Trades`
                  : "Square Off All Hedge Trades"}
              </h3>
              <p className="text-xs text-app-muted">
                {mode === "single"
                  ? "Independent Hedge Closure"
                  : mode === "selected"
                  ? "Targeted Selected Hedge Square-Off"
                  : "Bulk Hedge Square-Off Operation"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="p-1 text-app-muted hover:text-app-text transition-colors rounded-md"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {/* Main Trade Safety Alert */}
          <div className="p-3.5 rounded-lg bg-[#38BDF8]/10 border border-[#38BDF8]/30 flex items-start gap-3">
            <Warning size={20} className="text-[#38BDF8] shrink-0 mt-0.5" weight="fill" />
            <div className="text-xs space-y-1">
              <span className="font-semibold text-app-text block">Safety Protected Operation</span>
              <p className="text-app-muted leading-relaxed">
                Only positions identified with a <strong className="text-[#38BDF8]">HEDGE</strong> badge will be squared off. Your main/original strategy positions will remain completely active and untouched.
              </p>
            </div>
          </div>

          {/* Mode Specific Display */}
          {mode === "single" && trade && (
            <div className="border border-app-border bg-app-bg rounded-lg p-3.5 space-y-3 font-mono-d text-xs">
              <div className="flex items-center justify-between pb-2 border-b border-app-border">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded bg-[#0284C7]/20 border border-[#0284C7]/40 text-[10px] text-[#38BDF8] font-bold uppercase tracking-wider flex items-center gap-1">
                    <Shield size={12} weight="fill" /> HEDGE
                  </span>
                  <span className="text-app-muted">Ticket</span>
                  <span className="font-bold text-app-text">#{trade.ticket ?? trade.id}</span>
                </div>
                <span className={`font-semibold uppercase px-2 py-0.5 rounded text-[10px] ${isBuy ? "bg-blue-500/10 text-[#3B82F6]" : "bg-red-500/10 text-[#FF3B30]"}`}>
                  {isBuy ? "BUY HEDGE" : "SELL HEDGE"}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-app-muted block text-[11px]">Symbol</span>
                  <span className="font-semibold text-app-text">{trade.symbol}</span>
                </div>
                <div>
                  <span className="text-app-muted block text-[11px]">Volume (Lot)</span>
                  <span className="font-semibold text-app-text">{fmtNum(trade.lot)} L</span>
                </div>
                <div>
                  <span className="text-app-muted block text-[11px]">Entry Price</span>
                  <span className="font-semibold text-app-text">{fmtNum(trade.entry_price, 4)}</span>
                </div>
                <div>
                  <span className="text-app-muted block text-[11px]">Current Price</span>
                  <span className="font-semibold text-app-text">{fmtNum(trade.current_price, 4)}</span>
                </div>
                <div className="col-span-2 pt-1 border-t border-app-border/50 flex justify-between items-center">
                  <span className="text-app-muted text-[11px]">Current Hedge P/L</span>
                  <span className={`font-bold text-sm ${mtm >= 0 ? "text-[#10B981]" : "text-[#FF3B30]"}`}>
                    {fmtMoney(mtm)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {mode === "selected" && (
            <div className="border border-app-border bg-app-bg rounded-lg p-3.5 space-y-3 font-mono-d text-xs">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-2.5 bg-app-surface border border-app-border rounded-md text-center">
                  <span className="text-app-muted block text-[10px] uppercase">Selected Hedges</span>
                  <span className="text-base font-bold text-[#38BDF8]">{selectedCount}</span>
                </div>
                <div className="p-2.5 bg-app-surface border border-app-border rounded-md text-center">
                  <span className="text-app-muted block text-[10px] uppercase">Selected Volume</span>
                  <span className="text-base font-bold text-app-text">{fmtNum(selectedLots)} L</span>
                </div>
                <div className="p-2.5 bg-app-surface border border-app-border rounded-md text-center">
                  <span className="text-app-muted block text-[10px] uppercase">Selected MTM</span>
                  <span className={`text-base font-bold ${selectedMtm >= 0 ? "text-[#10B981]" : "text-[#FF3B30]"}`}>
                    {fmtMoney(selectedMtm)}
                  </span>
                </div>
              </div>
              <div className="max-h-48 overflow-y-auto space-y-2 pt-1">
                {selectedTrades.map((st) => (
                  <div
                    key={st.ticket ?? st.id}
                    className="p-2.5 bg-app-surface border border-app-border rounded-lg flex items-center justify-between text-[11px]"
                  >
                    <div className="flex items-center gap-2">
                      <span className="px-1.5 py-0.5 rounded bg-[#0284C7]/20 border border-[#0284C7]/40 text-[9px] text-[#38BDF8] font-bold uppercase tracking-wider shrink-0 flex items-center gap-1">
                        <Shield size={10} weight="fill" /> HEDGE
                      </span>
                      <span className="font-bold text-app-text">#{st.ticket ?? st.id}</span>
                      <span className="font-semibold">{st.symbol}</span>
                      <span className={`uppercase font-bold ${st.side === "buy" ? "text-[#3B82F6]" : "text-[#FF3B30]"}`}>
                        {(st.side || "buy").toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-semibold">{fmtNum(st.lot)} L</span>
                      <span className={`font-bold tabular-nums ${Number(st.profit || 0) >= 0 ? "text-[#10B981]" : "text-[#FF3B30]"}`}>
                        {fmtMoney(st.profit)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {mode === "all" && (
            <div className="border border-app-border bg-app-bg rounded-lg p-3.5 space-y-4 font-mono-d text-xs">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-2.5 bg-app-surface border border-app-border rounded-md text-center">
                  <span className="text-app-muted block text-[10px] uppercase">Active Hedges</span>
                  <span className="text-base font-bold text-[#38BDF8]">{effectiveHedgeCount}</span>
                </div>
                <div className="p-2.5 bg-app-surface border border-app-border rounded-md text-center">
                  <span className="text-app-muted block text-[10px] uppercase">Total Lots</span>
                  <span className="text-base font-bold text-app-text">{fmtNum(effectiveHedgeLots)} L</span>
                </div>
                <div className="p-2.5 bg-app-surface border border-app-border rounded-md text-center">
                  <span className="text-app-muted block text-[10px] uppercase">Overall Hedge MTM</span>
                  <span className={`text-base font-bold ${effectiveHedgeMtm >= 0 ? "text-[#10B981]" : "text-[#FF3B30]"}`}>
                    {fmtMoney(effectiveHedgeMtm)}
                  </span>
                </div>
              </div>

              {/* Running Positions List Breakdown */}
              <div className="space-y-2 pt-2 border-t border-app-border">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="font-bold text-app-text uppercase tracking-wider">
                    Running Positions ({runningTrades.length})
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded bg-[#0284C7]/20 border border-[#0284C7]/40 text-[9px] text-[#38BDF8] font-bold">
                      {hedgePositions.length} HEDGE(S) TO CLOSE
                    </span>
                    <span className="px-2 py-0.5 rounded bg-app-surface border border-app-border text-[9px] text-app-muted">
                      {mainPositions.length} MAIN (SAFE)
                    </span>
                  </div>
                </div>

                {fetching ? (
                  <div className="py-6 text-center text-app-muted font-mono-d text-xs flex items-center justify-center gap-2">
                    <CircleNotch size={14} className="animate-spin" /> Fetching running positions…
                  </div>
                ) : runningTrades.length === 0 ? (
                  <div className="py-6 text-center text-app-muted font-mono-d text-xs">
                    No active positions found across selected account(s).
                  </div>
                ) : (
                  <div className="max-h-56 overflow-y-auto space-y-2 pr-1">
                    {runningTrades.map((t) => {
                      const isHedge = Boolean(
                        t.is_hedge === true || String(t.is_hedge).toLowerCase() === "true" || t.magic === 999999 || String(t.magic) === "999999" || ["HEDGE", "HDG", "HUB"].some((kw) => String(t.comment || "").toUpperCase().includes(kw))
                      );
                      return (
                        <div
                          key={t.ticket ?? t.id}
                          className={`p-2.5 rounded-lg border font-mono-d text-xs flex items-center justify-between gap-2 ${
                            isHedge
                              ? "bg-[#0284C7]/10 border-[#0284C7]/40 text-app-text shadow-sm"
                              : "bg-app-surface/50 border-app-border/70 text-app-muted opacity-75"
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {isHedge ? (
                              <span className="px-2 py-0.5 rounded bg-[#0284C7]/20 border border-[#0284C7]/50 text-[9px] text-[#38BDF8] font-bold uppercase tracking-wider shrink-0 flex items-center gap-1">
                                <Shield size={10} weight="fill" /> HEDGE
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded bg-[#1E293B] border border-[#334155] text-[9px] text-app-muted uppercase font-semibold shrink-0">
                                MAIN (UNTOUCHED)
                              </span>
                            )}
                            <span className="font-bold text-app-text">#{t.ticket ?? t.id}</span>
                            <span className="font-semibold">{t.symbol}</span>
                            <span
                              className={`uppercase font-bold text-[11px] ${
                                t.side === "buy" ? "text-[#3B82F6]" : "text-[#FF3B30]"
                              }`}
                            >
                              {(t.side || "buy").toUpperCase()}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="font-semibold text-app-text">{fmtNum(t.lot)} L</span>
                            <span
                              className={`font-bold tabular-nums ${
                                Number(t.profit || 0) >= 0 ? "text-[#10B981]" : "text-[#FF3B30]"
                              }`}
                            >
                              {fmtMoney(t.profit)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <p className="text-[11px] text-app-muted text-center italic pt-1">
                This action will send a batch square-off command for all active hedge positions across {selectedAccountIds.length > 0 ? `${selectedAccountIds.length} selected` : "all active"} account(s).
              </p>
            </div>
          )}

          {/* Execution Result Banner */}
          {result && (
            <div
              className={`p-3.5 rounded-lg border text-xs ${
                result.success ? "bg-[#10B981]/10 border-[#10B981]/30 text-[#10B981]" : "bg-[#FF3B30]/10 border-[#FF3B30]/30 text-[#FF3B30]"
              }`}
            >
              <div className="flex items-center gap-2 font-bold mb-1">
                {result.success ? <CheckCircle size={16} /> : <WarningOctagon size={16} />}
                {result.success ? "Square-Off Queued Successfully" : "Square-Off Operation Failed"}
              </div>
              <p className="text-app-text text-[11px]">
                {result.message ||
                  (result.summary &&
                    `Queued close command for ${result.summary.total_hedges ?? effectiveHedgeCount} hedge position(s).`)}
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t border-app-border bg-app-bg/50 flex items-center justify-end gap-2.5 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-xs font-mono-d uppercase border border-app-border bg-app-surface hover:bg-app-hover text-app-text rounded-md transition-colors"
          >
            {result?.success ? "Close" : "Cancel"}
          </button>

          {!result?.success && (
            <button
              type="button"
              onClick={
                mode === "single"
                  ? handleConfirmSingle
                  : mode === "selected"
                  ? handleConfirmSelected
                  : handleConfirmAll
              }
              disabled={loading || (mode === "all" && effectiveHedgeCount === 0)}
              className="px-4 py-2 text-xs font-mono-d uppercase font-bold border border-[#FF3B30]/50 bg-[#FF3B30]/15 text-[#FF3B30] hover:bg-[#FF3B30]/25 rounded-md flex items-center gap-1.5 disabled:opacity-50 transition-colors"
            >
              {loading ? <CircleNotch size={14} className="animate-spin" /> : <Shield size={14} weight="fill" />}
              {mode === "single"
                ? "Confirm Square Off"
                : mode === "selected"
                ? `Confirm & Square Off (${selectedCount})`
                : "Square Off All Hedges"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
